import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, of, ReplaySubject} from 'rxjs';
import {DataService} from './data.service';
import {catchError, map, tap} from 'rxjs/operators';
import {Budget} from '../interfaces';

const CURRENT_FILTER_VERSION = 2;
const QUICK_BUDGET_IDS_STORAGE_KEY = 'quickBudgetListIds';

export interface BudgetListFilter {
    filter: string;
    status: string;
    showExport: boolean;
    sortActive?: string;
    sortDirection?: string;
    pageSize?: number;
    dateFrom?: string;
    dateTo?: string;
}

export interface StoredBudgetListFilter {
    version: number;
    filter: BudgetListFilter;
}

export interface BudgetListResponse {
    data: Budget[];
    totalSize: number;
}

const defaultFilter: BudgetListFilter = {
    filter: null,
    status: null,
    showExport: false,
    sortDirection: 'desc',
    sortActive: 'creation',
    pageSize: 20,
    dateFrom: null,
    dateTo: null,
};

@Injectable({
    providedIn: 'root'
})
export class BudgetListTableService {

    private appliedFilterAndSortingAndPagination: ReplaySubject<BudgetListFilter> = new ReplaySubject();
    private appliedFilter: BudgetListFilter;

    private performingQuickSerch: ReplaySubject<boolean> = new ReplaySubject();
    private performingSlowSearch: ReplaySubject<boolean> = new ReplaySubject();

    private quickBudgetListIdsSubject = new BehaviorSubject<string[]>(this.getInitialQuickBudgetListIds());
    public quickBudgetListIds$: Observable<string[]> = this.quickBudgetListIdsSubject.asObservable();

    constructor(
        private dataService: DataService
    ) {
        this.performingQuickSerch.next(true);
        this.performingSlowSearch.next(true);
    }

    public get appliedFilterAndSortingAndPagination$(): Observable<BudgetListFilter> {
        return this.appliedFilterAndSortingAndPagination.asObservable();
    }

    public loadData(mode: 'quick' | 'slow', pageIndex: number, pageSize: number): Observable<BudgetListResponse> {
        switch (mode) {
            case 'quick':
                return this.getQuickList();
            case 'slow':
                return this.getSlowList(pageIndex, pageSize);
        }
    }

    public isLoading$(mode: 'quick' | 'slow') {
        return (mode === 'quick') ? this.performingQuickSerch.asObservable() : this.performingSlowSearch.asObservable();
    }

    public setQuickBudgetListIds(ids: string[]): void {
        const cleanIds = ids || [];
        this.quickBudgetListIdsSubject.next(cleanIds);
        try {
            sessionStorage.setItem(QUICK_BUDGET_IDS_STORAGE_KEY, JSON.stringify(cleanIds));
        } catch (e) {
            console.error('Error saving quickBudgetListIds to sessionStorage', e);
        }
    }

    public getQuickBudgetListIds(): string[] {
        return this.quickBudgetListIdsSubject.value;
    }

    private getInitialQuickBudgetListIds(): string[] {
        try {
            const raw = sessionStorage.getItem(QUICK_BUDGET_IDS_STORAGE_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) {
                    return parsed;
                }
            }
        } catch (e) {
            console.error('Error reading quickBudgetListIds from sessionStorage', e);
        }
        return [];
    }

    retrieveFiltersFromStorage(): BudgetListFilter {
        const stored = localStorage.getItem('budgetListFilter');
        if (stored) {
            try {
                const storedFilter: StoredBudgetListFilter = JSON.parse(stored);
                if (storedFilter.version === CURRENT_FILTER_VERSION) {
                    this.appliedFilter = storedFilter.filter;
                    this.appliedFilterAndSortingAndPagination.next(this.appliedFilter);
                    return this.appliedFilter;
                }
            } catch (e) {
                console.error('Error parsing stored filter, falling back to default', e);
            }
        }

        // If no stored filter, version mismatch, or parsing error, return default.
        this.appliedFilter = defaultFilter;
        this.appliedFilterAndSortingAndPagination.next(this.appliedFilter);
        return this.appliedFilter;
    }

    storeFilters(filter: BudgetListFilter) {
        if (this.compareFilters(filter, this.appliedFilter)) {
            this.appliedFilter = {...this.appliedFilter, ...filter};

            const storedFilter: StoredBudgetListFilter = {
                version: CURRENT_FILTER_VERSION,
                filter: this.appliedFilter
            };

            localStorage.setItem('budgetListFilter', JSON.stringify(storedFilter));
            this.appliedFilterAndSortingAndPagination.next(this.appliedFilter);
        }
    }

    storeSortingAndPageSize(sortActive: string, sortDirection: string, pageSize: number) {
        this.storeFilters({
            ...this.appliedFilter,
            sortActive: sortActive,
            sortDirection: sortDirection,
            pageSize: pageSize
        });
    }

    compareFilters(actual: BudgetListFilter, old: BudgetListFilter) {
        if (!old) {
            return true;
        }
        return !Object.keys(actual).reduce((a: boolean, k: string) => a && actual[k] === old[k], true);
    }

    private getQuickList(): Observable<BudgetListResponse> {
        this.performingQuickSerch.next(true);
        return this.dataService.getQuickBudgetList().pipe(
            map((d) => ({data: d, totalSize: d.length})),
            tap(() => this.performingQuickSerch.next(false)),
            catchError((error) => {
                this.performingQuickSerch.next(false);
                console.error('Error getting quick orders list');
                console.error(error);
                return of({data: [], totalSize: 0});
            })
        );
    }

    private getSlowList(pageIndex: number, pageSize: number): Observable<BudgetListResponse> {
        console.log('getting slow data: ', pageIndex, pageSize);
        let startAt = pageIndex * pageSize;
        if (pageIndex !== 0) {
            startAt++;
        }
        this.performingSlowSearch.next(true);
        return this.dataService.getBudgetsListPaginated(pageSize, startAt, this.appliedFilter).pipe(
            tap(() => this.performingSlowSearch.next(false)),
            catchError((error) => {
                this.performingSlowSearch.next(false);
                console.error('Error getting slow orders list');
                console.error(error);
                return of({data: [], totalSize: 0});
            })
        );
    }

    async getHoursReportByBudgetListFilter() {
        console.log('Creating excel with filter: ', this.appliedFilter);
        return await this.dataService.getHoursReportByBudgetListFilter(this.appliedFilter).toPromise();
    }

    async getSummaryReportByBudgetListFilter() {
        console.log('Creating excel with filter: ', this.appliedFilter);
        return await this.dataService.getSummaryReportByBudgetListFilter(this.appliedFilter).toPromise();
    }
}
